import { useState, useRef, ChangeEvent, memo, useEffect } from 'react';
import {
  NextgenImage,
  FAIcon,
  Theme,
  Shade,
  IconStyle,
  useTranslation,
  IconAnimationClassName,
} from '@nextgen-web-framework/all';
import { ImageReturnType } from '@commerce-profile-webcomponents/sdk';
import { Logger, ConfigService } from '@nextgen-web-framework/core';
import { useGeneralConfig } from '../../utils/customHooks';
import ApiErrorModal from '../ApiErrorModal/ApiErrorModal';
import logObject from '../../../logObject';
import { iff, parseString } from '../../utils/helper';
import { ApiErrorModalProps } from '../ApiErrorModal/types/propTypes';
import ImageEditor from '../ImageEditor/ImageEditor';
import { CONSTANTS } from '../../utils/Constants';
import { convertImageToBase64 } from '../../utils/Utils';
import { ImageUploadProps } from './types/propTypes';
import styles from './ImageUpload.module.scss';

const ImageUpload = ({
  uploadLabel: uploadText,
  removeLabel,
  callback,
  showImage = true,
  uploadClass = '',
  wrapperClass = '',
  imageClass = '',
  sampleImage,
  loadingImage,
  removeIconHandler,
  testId,
  editorAspectRatio,
  disabled,
  canRemoveAfterSuccess = true,
  defaultImageBase64 = '',
  useReupload = false,
}: ImageUploadProps) => {
  const { defaultUploadLimit } = useGeneralConfig();
  const configs = ConfigService.getConfigs();
  const showImgPreview = Boolean(configs.NEXT_PUBLIC_PROFILE_IMAGE_PREVIEW);

  const logger = Logger.getInstance(logObject('ImageUpload.tsx', 'ImageUpload'));
  const t = useTranslation();
  const [showImageEditor, setShowImageEditor] = useState<boolean>(false);
  const [modalErrorData, setModalErrorData] = useState<ApiErrorModalProps>();
  const errorModelCloseHandler = () => {
    setImage('');
    // to reset state
    setModalErrorData(undefined);
  };
  const [image, setImage] = useState<File | string>('');
  const [showLoading, setShowLoading] = useState(false);
  const [uploadWasSuccess, setUploadWasSuccess] = useState(false);
  const [processedImage, setProcessedImage] = useState<File | string>(defaultImageBase64);
  const inputRef = useRef<HTMLInputElement>(null);
  const MAX_FILE_SIZE = defaultUploadLimit * 1024 * 1024;

  useEffect(() => {
    if (defaultImageBase64) {
      setProcessedImage(`data:image/png;base64,${defaultImageBase64}`);
      setShowLoading(false);
      setUploadWasSuccess(true);
    }
  }, []);

  const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target && e.target.files && e.target.files.length > 0) {
      const file = e.target.files;
      const isFileSizeValid = file[0].size <= MAX_FILE_SIZE; // Valid File Size
      const isFileTypeValid = CONSTANTS.VALID_FILE_TYPES.includes(file[0].type); // Valid File Type

      if (!isFileSizeValid) {
        setModalErrorData({
          headline: 'profile.ApiErrorModal.imageSizeError.headline',
          body: 'profile.ApiErrorModal.imageSizeError.body',
          primaryButtonCallback: errorModelCloseHandler,
        });
      }
      if (!isFileTypeValid) {
        setModalErrorData({
          headline: 'profile.ApiErrorModal.imageInvalidFormat.headline',
          body: 'profile.ApiErrorModal.imageInvalidFormat.body',
          primaryButtonCallback: errorModelCloseHandler,
        });
      }
      try {
        if (isFileSizeValid && isFileTypeValid) {
          await processUploadedFile(file[0]);
          e.target.value = '';
        } else {
          throw new Error('Invalid Image');
        }
      } catch (error) {
        setImage('');
        logger.error(error);
      }
    }

    // Reset input so selecting the same file again will trigger onChange
    e.target.value = '';
  };

  async function processUploadedFile(file: File) {
    setImage(file);
    if (showImgPreview) {
      setShowImageEditor(true);
    } else {
      const imgBase64 = await convertImageToBase64(file);
      callback(imgBase64 as string, setShowLoading, setUploadWasSuccess);
    }
  }

  function onConfirmImageClick(image: string | undefined) {
    if (image) {
      setProcessedImage(image);
      callback(image as string, setShowLoading, setUploadWasSuccess);
    }
    setShowImageEditor(false);
  }
  /*   useEffect(() => {
    if (clearImage) {
      setImage('');
      setProcessedImage('');
      setShowLoading(false);
      setUploadWasSuccess(false);
    }
  }, [clearImage]); */

  function removeImageHandlerInternal() {
    setImage('');
    setProcessedImage('');
    setShowLoading(false);
    setUploadWasSuccess(false);
  }

  function getImageSrc(
    isLoading: boolean,
    showPreview: boolean,
    uploadedImage: Blob,
    _processedImage: string,
    _defaultImage?: ImageReturnType,
    _loadingImage?: ImageReturnType,
  ): string {
    let _return: string | undefined;
    if (_loadingImage && isLoading) {
      _return = _loadingImage?.url;
    } else if (!uploadWasSuccess) {
      _return = _defaultImage?.url;
    } else if (showPreview) {
      _return = _processedImage ?? _defaultImage?.url;
    } else {
      if (uploadedImage) {
        _return = uploadedImage ? URL.createObjectURL(uploadedImage) : _defaultImage?.url;
      } else {
        _return = _processedImage ? _processedImage : _defaultImage?.url;
      }
    }
    return _return ?? '';
  }

  //removed for this PR review comment - https://github.com/AmwayCommon/commerce-profile-webcomponents/pull/1153#discussion_r1956975434
  /*   function getImageSrc() {
    const defaultImage = sampleImage?.url as string;
    const loadingImageUrl = loadingImage?.url as string;
    if (loadingImage && showLoading) {
      return loadingImageUrl;
    }
    if (showImagePreview) {
      //TODO ask Alisha why these two urls are different, string vs as blob.
      return uploadWasSuccess && processedImage ? (processedImage as string) : defaultImage;
    }
    return uploadWasSuccess && isImageUploaded ? URL.createObjectURL(image as Blob) : defaultImage;
  } */
  //showIcon is false only when its uploaded and we don't want to show remove icon.
  //canRemove is false for CID once it is uploaded.

  const handleRemoveOrUpload = () => {
    if (showRemoveIcon) {
      removeImageHandlerInternal();
      removeIconHandler?.();

      if (useReupload) {
        inputRef?.current?.click();
      }
    } else {
      inputRef?.current?.click();
    }
  };

  const showRemoveIcon = !showLoading && uploadWasSuccess && canRemoveAfterSuccess;
  const loadingIcon = iff(showLoading, 'spinner-third', 'upload');
  const iconName = iff(showRemoveIcon, 'xmark-large', loadingIcon);

  const removeText = removeLabel ?? t('profile.RegistrationPersonalDetailsStep.removeSign');
  const loadingText = iff(showLoading, t('profile.ImageUpload.uploadingLabel'), uploadText);
  const displayText = iff(showRemoveIcon, removeText, loadingText);

  return (
    <div
      className={`${styles['registration-id-upload-container']} ${wrapperClass}`}
      data-testid="registration-id-upload-container"
    >
      {showImage && (
        <div className={`${styles['upload-image-container']} ${imageClass}`} data-testid="upload-image-container">
          <NextgenImage
            src={getImageSrc(
              showLoading,
              showImgPreview ?? false,
              image as Blob,
              processedImage as string,
              sampleImage,
              loadingImage,
            )}
            alt={sampleImage?.altText as string}
            // height={CONSTANTS.IMAGE_HEIGHT} Height causing image to stretch
            width={CONSTANTS.IMAGE_WIDTH}
            fill={false}
            objectFit="contain"
          />
        </div>
      )}

      {image && showImageEditor && showImgPreview && (
        <ImageEditor
          imageFile={image}
          onConfirmImageClick={onConfirmImageClick}
          onImageEditorClose={() => setShowImageEditor(false)}
          editorAspectRatio={editorAspectRatio}
        />
      )}
      {/* show button to upload before upload was successful, and show remove icon if after success its allowed to remove.  */}
      {(canRemoveAfterSuccess || !uploadWasSuccess) && (
        <div
          className={`${styles['upload-cta']} ${uploadClass} ${disabled ? styles['disabled'] : ''}`}
          data-testid={`${parseString(testId)}upload-cta`}
          onClick={handleRemoveOrUpload}
          tabIndex={-1}
          aria-hidden="true"
        >
          <div className={styles['upload-icon']} data-testid="upload-icon">
            <FAIcon
              theme={Theme.PURPLE}
              iconName={iconName}
              shade={Shade.DARK}
              iconStyle={showLoading ? IconStyle.SOLID : IconStyle.REGULAR}
              className={`${styles['icon']} ${disabled ? styles['disabled'] : ''}`}
              iconAnimationClassName={showLoading ? IconAnimationClassName.SPIN : undefined}
            />
            <input
              ref={inputRef}
              data-testid="input-upload-testid"
              onChange={(_e) => handleFile(_e)}
              type="file"
              accept="image/*"
              className={styles['footer-input-upload']}
            />
          </div>
          <div className={`${styles['upload-text']} ${disabled ? styles['disabled'] : ''}`} data-testid="upload-text">
            {displayText}
          </div>
        </div>
      )}
      {(modalErrorData?.headline || modalErrorData?.body) && <ApiErrorModal {...modalErrorData} />}
    </div>
  );
};
export default memo(ImageUpload);
